import { Stack, Typography } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { t } from 'i18next';
import { get } from 'lodash';
import { useMemo, useState } from 'react';

import { AddContactGroupByEmail, UpsertContact } from 'contacts/components';
import { LISTBOX_STYLE } from 'contacts/constants';
import { ContactGroupType } from 'contacts/enums';
import { IContact, IContactGroup } from 'contacts/types';
import {
  DEFAULT_MIN_CHARS_TO_SEARCH,
  defaultRenderNewOption,
  IAutocompleteProps,
} from 'core/components';
import { FormField } from 'core/components';
import { TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { Color, Level } from 'theme/enums';

import { useRenderGroup } from './useRenderGroup';

interface IProps extends IAutocompleteProps {
  name: string;
  label?: string;
  onCreateNewOptionSuccess?: (option: IContact & { contactGroup?: IContactGroup }) => void;
  contactGroup?: IContactGroup;
  groupOptionsByContactGroup?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const CustomerContactFormField = ({
  name,
  label = t('labelCustomerContact'),
  requestPayload,
  onCreateNewOptionSuccess,
  contactGroup,
  groupOptionsByContactGroup = false,
  hidden = false,
  disabled = false,
  required = true,
  ...rest
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<
    ICorporateUserAutocomplete & IAutocompleteOption
  >(null);

  const { control, errors, setValue, watch } = useFormContext();

  const onClickAddContact = (option: ICorporateUserAutocomplete & IAutocompleteOption) => {
    if (option?.contactId && !option?.isNew) {
      return;
    }

    setIsOpen(true);
    setSelectedContact(option);

    setTimeout(() => {
      setValue(name, null);
    }, 1000);
  };

  const onSubmitSuccess = (contact: IContact, contactGroup?: IContactGroup) => {
    if (!contact?.id) {
      setValue(name, null);

      return;
    }

    const newValue = {
      ...contact,
      contactGroup: contactGroup,
      contactGroupId: contactGroup?.id,
    };

    const currentValue = watch(name);

    if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
      setValue(name, newValue);
    }

    onCreateNewOptionSuccess?.(newValue);
  };

  const onCloseAddContact = () => {
    setIsOpen(false);
    setSelectedContact(null);
  };

  const initialValuesAddContact = useMemo(() => {
    if (!selectedContact) {
      return {};
    }

    if (selectedContact?.isNew) {
      return {
        firstName: selectedContact?.title,
      };
    }

    return {};
  }, [selectedContact]);

  const renderOptions = (
    option: ICorporateUserAutocomplete,
    _: (option: ICorporateUserAutocomplete) => string,
    inputValue?: string
  ) => {
    return (
      <Stack onClick={() => onClickAddContact(option)} sx={{ gap: 1, width: '100%' }}>
        {option.id && (
          <TextHighlighter level={Level.TITLE_MD} text={option.name} highlight={inputValue} />
        )}

        {!option.id && (
          <Typography
            level={Level.TITLE_SM}
            color={Color.PRIMARY}
            fontWeight={600}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              lineHeight: '24px',
            }}
          >
            <Plus /> {t('labelAddContact')}
          </Typography>
        )}

        <TextHighlighter
          level={Level.BODY_SM}
          text={option.contactGroup?.name}
          highlight={inputValue}
        />
      </Stack>
    );
  };

  const groupBy = (option: ICorporateUserAutocomplete & IAutocompleteOption) => {
    if (option.isNew) {
      return 'new';
    }

    return option.mainContactGroup ? 'main' : 'other';
  };

  const renderGroup = useRenderGroup({
    contactGroupName: contactGroup?.name,
    mainGroupI18nKey: 'messageContactsAssociatedWithContactGroup',
    otherGroupI18nKey: 'messageContactsAssociatedWithOtherContactGroups',
  });

  const groupByParams = useMemo(() => {
    if (!groupOptionsByContactGroup || !contactGroup?.id) {
      return null;
    }

    return { groupBy, renderGroup };
  }, [groupOptionsByContactGroup, contactGroup?.id, renderGroup]);

  return (
    <>
      <FormField
        key={watch(name)}
        name={name}
        type={InputType.AUTOCOMPLETE}
        hidden={hidden}
        inputProps={{
          label,
          control,
          error: get(errors, name),
          disabled,
          getKey: (option: ICorporateUserAutocomplete) => `${option.id}-${option.contactGroup?.id}`,
          getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
          requestPayload: {
            ...requestPayload,
            filters: {
              userType: CorporateUserType.CUSTOMER,
              includeEmptyContactGroups: 1,
              ...requestPayload?.filters,
            },
          },
          renderOptions,
          renderNewOption: (option: IAutocompleteOption) =>
            defaultRenderNewOption(option, t('messageAddNewContact')),
          onNewOption: onClickAddContact,
          getLabel: (option: ICorporateUserAutocomplete) =>
            `${option.name} (${option.contactGroup?.name})`,
          required,
          listboxStyle: LISTBOX_STYLE,
          minCharsToSearch: DEFAULT_MIN_CHARS_TO_SEARCH,
          ...groupByParams,
          ...rest,
        }}
      />

      {contactGroup && (
        <UpsertContact
          isOpen={isOpen}
          onClose={onCloseAddContact}
          contactGroup={selectedContact?.contactGroup || contactGroup}
          onSubmitSuccess={onSubmitSuccess}
          initialValues={initialValuesAddContact}
          level={1}
        />
      )}

      {!contactGroup && (
        <AddContactGroupByEmail
          isOpen={isOpen}
          onClose={onCloseAddContact}
          contactGroupType={ContactGroupType.CUSTOMER}
          onSearchCustomerSuccess={onSubmitSuccess}
          searchContact
          level={1}
        />
      )}
    </>
  );
};
